import { useState, useEffect } from "react";
import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import {
  FaFileContract,
  FaCalendarCheck,
  FaUserTie,
  FaEye,
  FaSave,
  FaTimes,
  FaEdit,
  FaUser,
  FaGavel,
  FaInfoCircle,
  FaClipboardList,
  FaFileAlt,
  FaSearch,
  FaDownload,
  FaMailBulk,
  FaPlus,
  FaCheckCircle,
  FaClock,
  FaSync
} from "react-icons/fa";
import {
  FaRegFolderOpen,
  FaRegClock,
  FaRegCheckCircle
} from "react-icons/fa";
import "../styles/SolicitorAgreementPolicy.css";

export default function SolicitorAgreementPolicy() {
  const { token } = useAuth();

  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [agreementTitle, setAgreementTitle] = useState("");
  const [agreementStatement, setAgreementStatement] = useState("");
  const [terms, setTerms] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [solicitorProfile, setSolicitorProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("INCOMPLETE");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [agreements, setAgreements] = useState([]);
  let createToastId = null;

  useEffect(() => {
    fetchCompletedAppointments();
    fetchSolicitorProfile();
    fetchAllAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const storedAppointmentId = sessionStorage.getItem(
      "selected_appointment_for_agreement"
    );

    if (storedAppointmentId && appointments.length > 0) {
      const match = appointments.find(
        a => a.appointmentId === storedAppointmentId
      );

      if (match) {
        setSelectedAppointment(match);
      }

      sessionStorage.removeItem("selected_appointment_for_agreement");
    }
  }, [appointments]);

  const fetchCompletedAppointments = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/agreements/solicitor/completed`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await res.json();

      if (res.ok) {
        setAppointments(data.appointments || []);
      } else {
        toast.error(data.message || "Failed to load appointments");
      }

    } catch (err) {
      console.error(err);
      toast.error("Server error loading appointments");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAgreements = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/agreements/solicitor/my`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        const data = await res.json();
        setAgreements(data.agreements || []);
      }
    } catch (error) {
      console.error("Fetch agreements error:", error);
    }
  };


  const fetchSolicitorProfile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      if (res.ok) {
        setSolicitorProfile(data.user);
      } else {
        toast.error("Failed to load solicitor profile");
      }

    } catch (err) {
      console.error(err);
      toast.error("Error loading solicitor profile");
    }
  };

  const handleRefresh = async () => {
    setRefreshLoading(true);
    await Promise.all([
      fetchCompletedAppointments(),
      fetchAllAgreements()
    ]);
    setRefreshLoading(false);
    toast.success("Data refreshed successfully");
  };

  const handleSelect = (id) => {
    const app = appointments.find(a => a.appointmentId === id);
    setSelectedAppointment(app);
  };

  const openPreview = async () => {
    if (!selectedAppointment) {
      toast.error("Please select appointment");
      return;
    }

    if (!agreementTitle.trim()) {
      toast.error("Enter agreement title");
      return;
    }

    if (!agreementStatement.trim()) {
      toast.error("Enter agreement statement");
      return;
    }

    if (!terms.trim()) {
      toast.error("Enter terms & conditions");
      return;
    }

    await fetchSolicitorProfile();
    setShowPreview(true);
  };

  const saveAgreement = async () => {
    try {
      setSaving(true);
      createToastId = toast.loading("Creating agreement...");

      const res = await fetch(
        `${API_BASE_URL}/api/agreements/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            appointmentId: selectedAppointment.appointmentId,
            agreementTitle,
            agreementBody: agreementStatement,
            termsAndConditions: terms
          })
        }
      );

      const data = await res.json();

      if (res.ok) {
        toast.update(createToastId, {
          render: `Agreement created successfully (ID: ${data.agreement?.agreementId || "Generated"})`,
          type: "success",
          isLoading: false,
          autoClose: 3000
        });

        setShowPreview(false);
        setSelectedAppointment(null);
        setAgreementTitle("");
        setAgreementStatement("");
        setTerms("");

        fetchCompletedAppointments();
        fetchAllAgreements();
      } else {
        toast.error(data.message || "Failed to Create agreement");
      }

    } catch (err) {
      console.error(err);
      toast.error("Server error saving agreement");
    } finally {
      setSaving(false);
    }
  };

  const handleView = async (agreementId) => {
    const res = await fetch(
      `${API_BASE_URL}/api/agreements/solicitor/download/${agreementId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };


  const handleDownload = async (agreementId) => {
    const res = await fetch(
      `${API_BASE_URL}/api/agreements/solicitor/download/${agreementId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${agreementId}.pdf`;
    a.click();

    URL.revokeObjectURL(url);
  };


  const handleEmail = async (agreementId) => {
    try {

      const res = await fetch(
        `${API_BASE_URL}/api/agreements/solicitor/mail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ agreementId })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to send mail");
      }

      toast.success("Agreement successfully sent to client!", {
        autoClose: 3000
      });

    } catch (error) {
      console.error("Mail error:", error);
      toast.error(
        error.message || "❌ Failed to send agreement to client",
        { autoClose: 3000 }
      );
    }
  };



  const filteredAppointments = appointments.filter(app =>
    app.appointmentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.userDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.caseType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const incompleteAgreements = agreements.filter(
    ag => ag.status === "SIGNED_BY_SOLICITOR"
  );

  const completedAgreements = agreements.filter(
    ag => ag.status === "SIGNED_BY_BOTH"
  );

  const visibleAgreements = activeTab === "INCOMPLETE"
    ? incompleteAgreements
    : completedAgreements;

  const stats = {
    total: agreements.length,
    incomplete: incompleteAgreements.length,
    completed: completedAgreements.length,
    pendingAppointments: appointments.length
  };

  return (
    <div className="agreement-policy-container">
      {/* Header */}
      <div className="agreement-policy-header">
        <div className="agreement-policy-header-content">
          <div>
            <h1 className="agreement-policy-title">Agreement Management</h1>
            <p className="agreement-policy-subtitle">Create, manage, and track legal agreements</p>
          </div>
          <div className="agreement-policy-header-actions">
            <button
              className="agreement-policy-refresh-btn"
              onClick={handleRefresh}
              disabled={refreshLoading}
            >
              <FaSync className={refreshLoading ? "spinning" : ""} />
              <span>{refreshLoading ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>
      </div>


      {/* Stats Cards */}
      <div className="agreement-policy-stats-grid">

        <div className="agreement-policy-stat-card stat-total">
          <div className="agreement-policy-stat-icon">
            <FaRegFolderOpen size={24} />
          </div>
          <div className="agreement-policy-stat-content">
            <h3 className="agreement-policy-stat-value">{stats.total}</h3>
            <p className="agreement-policy-stat-label">Total Agreements</p>
          </div>
        </div>

        <div className="agreement-policy-stat-card stat-incomplete">
          <div className="agreement-policy-stat-icon">
            <FaRegClock size={24} />
          </div>
          <div className="agreement-policy-stat-content">
            <h3 className="agreement-policy-stat-value">{stats.incomplete}</h3>
            <p className="agreement-policy-stat-label">Awaiting Client Signature</p>
          </div>
        </div>

        <div className="agreement-policy-stat-card stat-completed">
          <div className="agreement-policy-stat-icon">
            <FaRegCheckCircle size={24} />
          </div>
          <div className="agreement-policy-stat-content">
            <h3 className="agreement-policy-stat-value">{stats.completed}</h3>
            <p className="agreement-policy-stat-label">Completed Agreements</p>
          </div>
        </div>

      </div>



      {/* Main Content */}
      <div className="agreement-policy-content">
        {/* Tabs Section */}
        <div className="agreement-policy-tabs-section">
          <div className="agreement-policy-tabs-header">
            <div className="agreement-policy-tabs-container">
              <button
                className={`agreement-policy-tab-btn ${activeTab === "INCOMPLETE" ? "active" : ""}`}
                onClick={() => setActiveTab("INCOMPLETE")}
              >
                <FaClock size={14} />
                <span>Awaiting Signature ({stats.incomplete})</span>
              </button>

              <button
                className={`agreement-policy-tab-btn ${activeTab === "COMPLETED" ? "active" : ""}`}
                onClick={() => setActiveTab("COMPLETED")}
              >
                <FaCheckCircle size={14} />
                <span>Completed Agreements ({stats.completed})</span>
              </button>

              <button
                className={`agreement-policy-tab-btn ${activeTab === "CREATE" ? "active" : ""}`}
                onClick={() => setActiveTab("CREATE")}
              >
                <FaPlus size={14} />
                <span>Create New Agreement</span>
              </button>
            </div>
          </div>

          <div className="agreement-policy-tabs-content">
            {/* Agreements List - INCOMPLETE & COMPLETED */}
            {(activeTab === "INCOMPLETE" || activeTab === "COMPLETED") && (
              <div className="agreement-policy-list-section">
                <div className="agreement-policy-list-header">
                  <div className="agreement-policy-search-container">
                    <FaSearch className="agreement-policy-search-icon" />
                    <input
                      type="text"
                      className="agreement-policy-search-input"
                      placeholder="Search by Agreement ID, Client Name, or Case Type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {loading ? (
                  <div className="agreement-policy-loading">
                    <div className="agreement-policy-spinner"></div>
                    <p>Loading agreements...</p>
                  </div>
                ) : visibleAgreements.length === 0 ? (
                  <div className="agreement-policy-empty-state">
                    <FaFileContract size={48} />
                    <p>No {activeTab === "INCOMPLETE" ? "incomplete" : "completed"} agreements found</p>
                  </div>
                ) : (
                  <div className="agreement-policy-agreements-grid">
                    {visibleAgreements.map((agreement) => (
                      <div key={agreement._id} className="agreement-policy-agreement-card">
                        <div className="agreement-policy-agreement-header">
                          <div className="agreement-policy-agreement-id">
                            <FaFileAlt size={16} style={{ color: "#bfa56a" }} />
                            <span>{agreement.agreementId}</span>
                          </div>
                          <div className={`agreement-policy-status-badge ${agreement.status.toLowerCase()}`}>
                            {agreement.status === "SIGNED_BY_BOTH" ? "Completed" : "Awaiting Signature"}
                          </div>
                        </div>

                        <div className="agreement-policy-agreement-body">
                          <div className="agreement-policy-client-info">
                            <div className="agreement-policy-client-name">
                              <FaUser size={12} />
                              <span>{agreement.userDetails?.name || "Unknown Client"}</span>
                            </div>
                            <div className="agreement-policy-case-type">
                              <FaGavel size={12} />
                              <span>{agreement.caseType || "General Case"}</span>
                            </div>
                          </div>

                          <div className="agreement-policy-date-info">
                            <div className="agreement-policy-created-date">
                              <FaCalendarCheck size={12} />
                              <span>Created: {new Date(agreement.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>

                          <div className="agreement-policy-actions">
                            <button
                              className="agreement-policy-action-btn view"
                              onClick={() => handleView(agreement.agreementId)}
                            >
                              <FaEye size={14} />
                              <span>View</span>
                            </button>
                            <button
                              className="agreement-policy-action-btn download"
                              onClick={() => handleDownload(agreement.agreementId)}
                            >
                              <FaDownload size={14} />
                              <span>Download</span>
                            </button>
                            <button
                              className="agreement-policy-action-btn email"
                              onClick={() => handleEmail(agreement.agreementId)}
                            >
                              <FaMailBulk size={14} />
                              <span>Mail to Client</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Create New Agreement Section */}
            {activeTab === "CREATE" && (
              <>
                {/* Appointment Selection */}
                <div className="agreement-policy-card">
                  <div className="agreement-policy-card-header">
                    <div className="agreement-policy-card-icon">
                      <FaCalendarCheck size={20} />
                    </div>
                    <div>
                      <h3 className="agreement-policy-card-title">Select Appointment</h3>
                      <p className="agreement-policy-card-subtitle">Choose completed appointment to create agreement</p>
                    </div>
                  </div>

                  <div className="agreement-policy-search-dropdown">
                    <div className="agreement-policy-search-wrapper">
                      <FaSearch className="search-dropdown-icon" />
                      <input
                        type="text"
                        className="agreement-policy-search-dropdown-input"
                        placeholder="Search appointment ID, client name, or case type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <select
                      className="agreement-policy-select"
                      onChange={(e) => handleSelect(e.target.value)}
                      value={selectedAppointment?.appointmentId || ""}
                    >
                      <option value="">Select Appointment</option>
                      {filteredAppointments.map(app => (
                        <option key={app.appointmentId} value={app.appointmentId}>
                          {app.appointmentId} - {app.userDetails?.name} - {app.caseType}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Selected Appointment Details */}
                {selectedAppointment && (
                  <div className="agreement-policy-card">
                    <div className="agreement-policy-card-header">
                      <div className="agreement-policy-card-icon">
                        <FaUserTie size={20} />
                      </div>
                      <div>
                        <h3 className="agreement-policy-card-title">Appointment Details</h3>
                        <p className="agreement-policy-card-subtitle">Review user and case information</p>
                      </div>
                    </div>

                    <div className="agreement-policy-details-grid">
                      <div className="agreement-policy-details-section">
                        <h4 className="agreement-policy-details-title">
                          <FaUser size={14} />
                          <span>User Details</span>
                        </h4>
                        <div className="agreement-policy-details-row">
                          <span className="agreement-policy-details-label">Name:</span>
                          <span className="agreement-policy-details-value">{selectedAppointment.userDetails.name}</span>
                        </div>
                        <div className="agreement-policy-details-row">
                          <span className="agreement-policy-details-label">Email:</span>
                          <span className="agreement-policy-details-value">{selectedAppointment.userDetails.email}</span>
                        </div>
                        <div className="agreement-policy-details-row">
                          <span className="agreement-policy-details-label">Mobile:</span>
                          <span className="agreement-policy-details-value">{selectedAppointment.userDetails.mobile}</span>
                        </div>
                      </div>

                      <div className="agreement-policy-details-section">
                        <h4 className="agreement-policy-details-title">
                          <FaGavel size={14} />
                          <span>Case Details</span>
                        </h4>
                        <div className="agreement-policy-details-row">
                          <span className="agreement-policy-details-label">Practice Area:</span>
                          <span className="agreement-policy-details-value">{selectedAppointment.practiceArea}</span>
                        </div>
                        <div className="agreement-policy-details-row">
                          <span className="agreement-policy-details-label">Case Type:</span>
                          <span className="agreement-policy-details-value">{selectedAppointment.caseType}</span>
                        </div>
                        <div className="agreement-policy-details-row">
                          <span className="agreement-policy-details-label">Description:</span>
                          <span className="agreement-policy-details-value">{selectedAppointment.caseDescription}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Agreement Inputs */}
                {selectedAppointment && (
                  <div className="agreement-policy-card">
                    <div className="agreement-policy-card-header">
                      <div className="agreement-policy-card-icon">
                        <FaFileAlt size={20} />
                      </div>
                      <div>
                        <h3 className="agreement-policy-card-title">Agreement Content</h3>
                        <p className="agreement-policy-card-subtitle">Define agreement terms and conditions</p>
                      </div>
                    </div>

                    <div className="agreement-policy-form-group">
                      <label className="agreement-policy-label">
                        <FaInfoCircle size={14} />
                        <span>Agreement Title</span>
                      </label>
                      <input
                        type="text"
                        className="agreement-policy-input"
                        placeholder="Agreement Title"
                        value={agreementTitle}
                        onChange={(e) => setAgreementTitle(e.target.value)}
                      />
                    </div>

                    <div className="agreement-policy-form-group">
                      <label className="agreement-policy-label">
                        <FaFileContract size={14} />
                        <span>Agreement Statement</span>
                      </label>
                      <textarea
                        className="agreement-policy-textarea"
                        placeholder="Agreement Statement"
                        rows={6}
                        value={agreementStatement}
                        onChange={(e) => setAgreementStatement(e.target.value)}
                      />
                    </div>

                    <div className="agreement-policy-form-group">
                      <label className="agreement-policy-label">
                        <FaClipboardList size={14} />
                        <span>Terms & Conditions</span>
                      </label>
                      <textarea
                        className="agreement-policy-textarea"
                        placeholder="Terms & Conditions"
                        rows={5}
                        value={terms}
                        onChange={(e) => setTerms(e.target.value)}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="agreement-policy-actions">
                      <button
                        className="agreement-policy-preview-btn"
                        onClick={openPreview}
                        disabled={!selectedAppointment || !agreementTitle.trim() || !agreementStatement.trim() || !terms.trim()}
                      >
                        <FaEye className="agreement-policy-btn-icon" />
                        <span>Preview Agreement</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="agreement-policy-preview-modal">
          <div className="agreement-policy-preview-content">
            <div className="agreement-policy-preview-header">
              <div>
                <h2 className="agreement-policy-preview-title">{agreementTitle}</h2>
              </div>
              <button
                className="agreement-policy-preview-close"
                onClick={() => setShowPreview(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="agreement-policy-preview-body">
              {/* User Details */}
              <h4>Client Details</h4>
              <p>Name: {selectedAppointment.userDetails.name}</p>
              <p>Email: {selectedAppointment.userDetails.email}</p>
              <p>Mobile: {selectedAppointment.userDetails.mobile}</p>

              {/* Case Details */}
              <h4>Case Details</h4>
              <p>Practice Area: {selectedAppointment.practiceArea}</p>
              <p>Case Type: {selectedAppointment.caseType}</p>
              <p>Description: {selectedAppointment.caseDescription}</p>

              <h4>Agreement Statement</h4>
              <p className="agreement-policy-preview-text">{agreementStatement}</p>

              {/* Terms */}
              <h4>Terms & Conditions</h4>
              <p className="agreement-policy-preview-text">{terms}</p>

              <div className="agreement-policy-signatures-section">
                <div className="agreement-policy-signatures-grid">
                  {/* User Side */}
                  <div className="agreement-policy-signature-box">
                    <p><strong>Client Signature</strong></p>
                    <div className="agreement-policy-signature-line"></div>
                    <p className="agreement-policy-signature-date">
                      Signed Date: Pending
                    </p>
                  </div>

                  {/* Solicitor Side */}
                  <div className="agreement-policy-signature-box">
                    <p><strong>Name:</strong> {solicitorProfile?.name}</p>
                    <p><strong>Email:</strong> {solicitorProfile?.email}</p>

                    <div className="agreement-policy-signature-field">
                      <p><strong>Solicitor Signature</strong></p>
                      {solicitorProfile?.profile?.signature ? (
                        <img
                          src={solicitorProfile.profile.signature}
                          alt="Solicitor Signature"
                          className="agreement-policy-signature-image"
                        />
                      ) : (
                        <div className="agreement-policy-signature-line"></div>
                      )}
                      <p className="agreement-policy-signature-date">
                        Signed Date: {new Date().toDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="agreement-policy-preview-actions">
                <button
                  className="agreement-policy-edit-btn"
                  onClick={() => setShowPreview(false)}
                >
                  <FaEdit className="agreement-policy-btn-icon" />
                  <span>Edit</span>
                </button>

                <button
                  className="agreement-policy-save-btn"
                  onClick={saveAgreement}
                  disabled={saving}
                >
                  <FaSave className="agreement-policy-btn-icon" />
                  <span>{saving ? "Saving..." : "Create Agreement"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}